import { Tooltip as TooltipPrimitive } from 'radix-ui'
import { type ComponentProps, createContext, type ReactElement, type ReactNode, use } from 'react'

import { cn } from '../lib/cn'

const ProviderPresent = createContext(false)

export type TooltipProviderProps = ComponentProps<typeof TooltipPrimitive.Provider>

/**
 * Coloque uma vez na raiz do app: os tooltips passam a compartilhar o atraso (quem já viu um
 * tooltip vê o próximo sem esperar). Sem ele, cada `Tooltip` cria o próprio provider.
 */
export function TooltipProvider({
  delayDuration = 400,
  skipDelayDuration = 300,
  children,
  ...props
}: TooltipProviderProps) {
  return (
    <TooltipPrimitive.Provider
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      {...props}
    >
      <ProviderPresent value>{children}</ProviderPresent>
    </TooltipPrimitive.Provider>
  )
}

export interface TooltipProps extends Omit<
  ComponentProps<typeof TooltipPrimitive.Root>,
  'children'
> {
  /** Texto curto do tooltip. Vazio: renderiza só o gatilho. */
  content: ReactNode
  /** Gatilho: um único elemento focável (botão, link). */
  children: ReactElement
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  sideOffset?: number
  className?: string
}

export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  sideOffset = 6,
  className,
  ...rootProps
}: TooltipProps) {
  const hasProvider = use(ProviderPresent)
  if (content == null || content === '' || content === false) return children

  const tooltip = (
    <TooltipPrimitive.Root {...rootProps}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={8}
          className={cn(
            'z-50 max-w-64 origin-(--radix-tooltip-content-transform-origin) rounded-sm bg-ink px-2 py-1 text-compact text-canvas shadow-float',
            'data-[state=delayed-open]:animate-float-in data-[state=instant-open]:animate-float-in data-[state=closed]:animate-float-out',
            className,
          )}
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )

  return hasProvider ? tooltip : <TooltipPrimitive.Provider>{tooltip}</TooltipPrimitive.Provider>
}
